// Sentry sidecar — MUST be the very first import in `index.js` so the SDK
// installs its global error handlers before any React / router / axios
// code runs.
//
// Per Sentry's official React SDK guide (skills.sentry.dev):
//  • React 19 → use `reactErrorHandler()` on `createRoot` (exported below)
//  • React Router v7 (non-framework mode) → `reactRouterV7BrowserTracingIntegration`
//  • Init should live in a dedicated sidecar file (`instrument.js`), not
//    inline with app bootstrap code.
//
// PII: `sendDefaultPii: false` plus a `beforeSend` scrubber redact
// password/Authorization/JWT/token/secret keys before events leave the
// browser. Frontend Sentry is production-gated (`NODE_ENV=production` AND
// `REACT_APP_SENTRY_ENVIRONMENT=production`) — preview & dev builds are
// intentionally silent.

import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

const SENSITIVE_KEYS = new Set([
  "password", "passwd", "pwd",
  "authorization", "auth",
  "token", "access_token", "refresh_token", "id_token", "jwt", "session",
  "secret", "api_key", "apikey",
]);

const JWT_RE = /eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9\-_.]+/g;

const scrubString = (s) =>
  s.replace(JWT_RE, "[REDACTED_JWT]").replace(BEARER_RE, "Bearer [REDACTED]");

const walk = (value) => {
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map(walk);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = SENSITIVE_KEYS.has(String(k).toLowerCase()) ? "[REDACTED]" : walk(v);
    }
    return out;
  }
  return value;
};

const dsn = process.env.REACT_APP_SENTRY_DSN;
// Env tag is only a label in Sentry, NOT a gate. Gate on NODE_ENV alone
// so a fresh production deploy always sends without needing an extra
// build-time override to be set on the deployment host.
const env =
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  (process.env.NODE_ENV === "production" ? "production" : "development");
const enabled = !!dsn && process.env.NODE_ENV === "production";

if (enabled) {
  Sentry.init({
    dsn,
    environment: env,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    integrations: [
      // Route-aware tracing for React Router v7 (non-framework mode) →
      // gives us named transactions like `GET /facilities` instead of the
      // generic `GET /`, and captures navigation spans automatically.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Attach `sentry-trace` / `baggage` headers to outgoing API calls so
    // frontend errors link back to the backend span that served them.
    tracePropagationTargets: [
      /^\/api\//,
      /\.preview\.emergentagent\.com\/api\//,
      /\.emergent\.host\/api\//,
    ],
    // Filter out noise from third-party browser extensions (Honey, price
    // trackers, ad blockers) — they dispatch CustomEvents that Sentry's
    // global `onunhandledrejection` handler picks up but aren't our bugs.
    ignoreErrors: [
      // Honey extension emits these — the string form Sentry uses when the
      // rejection reason is a CustomEvent it can't stringify further.
      /^Event `?CustomEvent`?(\s+\(type=unhandledrejection\))?/i,
      "Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted",
      // ResizeObserver noise (browser bug, benign)
      /ResizeObserver loop /,
    ],
    beforeSend(event, hint) {
      // Drop unhandled rejections whose reason is a CustomEvent (Honey &
      // similar extension injections). We check both the raw `originalException`
      // and the serialised exception `type` so this holds across SDK versions.
      try {
        const original = hint?.originalException;
        if (typeof CustomEvent !== "undefined" && original instanceof CustomEvent) {
          return null;
        }
        const exType = event?.exception?.values?.[0]?.type;
        if (exType === "CustomEvent" || exType === "Event") return null;
      } catch { /* fall through to scrubbing */ }
      try { return walk(event); } catch { return event; }
    },
  });
} else {
  // eslint-disable-next-line no-console
  console.info("[Sentry] disabled (env=%s node=%s dsn=%s)", env, process.env.NODE_ENV, dsn ? "set" : "unset");
}

// Re-export the React 19 error handler and ErrorBoundary so `index.js`
// can plug them into `createRoot` without importing @sentry/react twice.
export const sentryReactErrorHandler = enabled ? Sentry.reactErrorHandler : null;
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const sentryEnabled = enabled;

// Rebuild marker: Emergent Managed Google Auth
